<template>
  <!-- Facet group container with gray background -->
  <div class="mb-4 p-3 bg-gray-50">
    <!-- Facet group title/display name -->
    <div class="text-lg mb-4 font-bold">
      {{ displayName }}
    </div>

    <!-- Filter input - only shown when there are more than 7 items -->
    <label>
      <span class="sr-only">Filter {{ displayName }}</span>
      <input
        v-if="showFilter"
        v-model="filterValue"
        type="text"
        class="mb-2 w-full px-2 py-1 border border-gray-200"
        placeholder="Filter..."
      >
    </label>

    <!-- List of facet options with checkboxes -->
    <div>
      <label
        v-for="([label, count]) in visibleItems"
        :key="label"
        class="flex gap-2 text-gray-900 mb-2 cursor-pointer"
      >
        <!-- Checkbox for each facet option -->
        <input
          type="checkbox"
          class="my-auto"
          :checked="isActive(label)"
          @change="toggleFacet(label)"
        >
        <!-- Facet label with count in parentheses -->
        <span
          v-if="displayName === 'Asset Type'"
          :class="`credential-type credential-${label.toLowerCase()}`"
          class="my-auto"
        >
          <svg
            viewBox="0 0 30 30"
            :class="`icon-credential-${label.toLowerCase()}`"
            :title="`icon-credential-${label.toLowerCase()}`"
            :aria-label="`${label} credential type icon`"
          >
            <use :href="`#icon-credential-${label.toLowerCase()}`"></use>
          </svg>
        </span>
        <span class="my-auto">{{ label }} ({{ count.toLocaleString() }})</span>
      </label>
    </div>

    <!-- Show more/less button - only displayed when there are more than 7 items -->
    <div
      v-if="items.length > 7"
      class="border-t mt-4 pt-2"
    >
      <button
        class="link link-primary link-red link-cta uppercase bg-transparent border-none cursor-pointer"
        :class="expanded ? 'link-cta-up' : 'link-cta-down'"
        @click.prevent="toggleExpand"
      >
        {{ expanded ? 'Show less' : 'Show more' }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'

// Component props - data passed from parent component
const props = defineProps<{
  facetName: string                    // Internal name of the facet (e.g., 'sei_year_published')
  facetObj: Record<string, number>     // Object mapping facet values to their counts
  selected: string[]                   // Array of currently selected facet values
  expanded: boolean                    // Whether the facet group is expanded to show all items
}>()

// Events emitted to parent component
const emit = defineEmits<{
  (e: 'update:selected', value: string): void    // Emitted when a facet option is toggled
  (e: 'update:expanded', value: boolean): void   // Emitted when expand/collapse state changes
}>()

// Mapping of internal facet names to user-friendly display names
const facetDisplayNames: Record<string, string> = {
  AssetTypeName: 'Asset Type',
  sei_tags: 'Topic',
}

// Computed properties for reactive data
const displayName = computed(() => facetDisplayNames[props.facetName] || props.facetName)
const items = computed(() => Object.entries(props.facetObj))
const filterValue = ref('')
// Show filter input only when there are more than 7 items
const showFilter = computed(() => items.value.length > 7)

// Filter items based on user input in the filter field
const filteredItems = computed(() => {
  if (!showFilter.value || !filterValue.value.trim()) return items.value
  const val = filterValue.value.trim().toLowerCase()
  return items.value.filter(([label]) => label.toLowerCase().includes(val))
})

// Determine which items to display based on filtering, sorting, and expand state
const visibleItems = computed(() => {
  const items = [...filteredItems.value] // make a copy to avoid mutating original


  // Remove "None" option if present (cleanup for data quality)
  for (const i in items) {
    if (items[i][0].toLowerCase() === 'none') {
      items.splice(Number(i), 1);
      break;
    }
  }

  // Return all items if expanded, otherwise limit to first 7
  return props.expanded ? items : items.slice(0, 7)
})

// Check if a specific facet value is currently selected
function isActive(label: string) {
  return props.selected.includes(label)
}

// Toggle selection state of a facet option
function toggleFacet(label: string) {
  emit('update:selected', label)
}

// Toggle expanded/collapsed state of the facet group
function toggleExpand() {
  emit('update:expanded', !props.expanded)
}
</script>
